// src/authpages/group/VideoCall.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, 
  Camera, 
  CameraOff, 
  Mic, 
  MicOff, 
  PhoneOff,
  Users,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { VideoCallService } from './VideoCallService';
import { initializeVideoCall } from './FirebaseVideoSetup';

const VideoCall = ({ 
  group, 
  currentUserId, 
  getFriendNameById,
  onCallEnd = () => {} 
}) => {
  // State
  const [callService, setCallService] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState('initializing'); // initializing, connecting, active, ended, error
  const [activeParticipants, setActiveParticipants] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState(null);
  
  // Camera handling states
  const [deviceList, setDeviceList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [useCanvas, setUseCanvas] = useState(false);

  // Refs
  const localVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const videoContainerRef = useRef(null);
  
  // Initialize call service
  useEffect(() => {
    if (!group || !currentUserId) return;
    
    const service = new VideoCallService(group.id, currentUserId);
    setCallService(service);
    
    // Find available cameras
    findCameras();
    
    // Set handler for remote streams
    service.setOnRemoteStream((userId, stream) => {
      console.log(`Remote stream added for user ${userId}`);
      setRemoteStreams(prev => ({
        ...prev,
        [userId]: stream
      }));
      
      // Update active participants list
      setActiveParticipants(prev => {
        if (!prev.includes(userId)) {
          return [...prev, userId];
        }
        return prev;
      });
    });
    
    // Set handler for user disconnection
    service.setOnRemoteUserDisconnected((userId) => {
      console.log(`User ${userId} disconnected`);
      setRemoteStreams(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      
      // Update active participants list
      setActiveParticipants(prev => 
        prev.filter(id => id !== userId)
      );
    });
    
    return () => {
      // Cleanup on unmount
      if (service) {
        service.endCall().catch(error => {
          console.error("Error ending call during cleanup:", error);
        });
      }
      
      // Stop canvas animation if active
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [group, currentUserId]);
  
  // Function to enumerate available devices
  const findCameras = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setDeviceList(videoDevices);
      console.log("Available video devices:", videoDevices);
      
      if (videoDevices.length > 0 && !selectedCamera) {
        setSelectedCamera(videoDevices[0].deviceId);
      }
      
      return videoDevices;
    } catch (error) {
      console.error("Error finding cameras:", error);
      return [];
    }
  };
  
  // Canvas-based video rendering (as a fallback)
  useEffect(() => {
    if (!useCanvas || !localStream || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const videoEl = document.createElement('video');
    
    videoEl.srcObject = localStream;
    videoEl.muted = true;
    videoEl.play().catch(err => console.error("Error playing video for canvas:", err));
    
    const drawFrame = () => {
      if (videoEl.readyState === videoEl.HAVE_ENOUGH_DATA) {
        // Set canvas dimensions to match video
        if (canvas.width !== videoEl.videoWidth || canvas.height !== videoEl.videoHeight) {
          canvas.width = videoEl.videoWidth || 640;
          canvas.height = videoEl.videoHeight || 480;
        }
        
        // Draw video frame to canvas
        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      }
      
      // Request next frame
      animationRef.current = requestAnimationFrame(drawFrame);
    };
    
    // Start drawing
    drawFrame();
    
    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      videoEl.pause();
      videoEl.srcObject = null;
    };
  }, [useCanvas, localStream]);
  
  // Debug useEffect to log and force video playback
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      console.log("Setting stream to video element:", localVideoRef.current);
      console.log("Stream tracks:", localStream.getTracks().map(track => ({
        kind: track.kind,
        enabled: track.enabled,
        label: track.label
      })));
      
      // Force play on the video element
      const playPromise = localVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Error playing video:", error);
        });
      }
    }
  }, [localStream]);
  
  // Manual play on video click (for browsers that block autoplay)
  const manualPlayVideo = async () => {
    if (localVideoRef.current && localStream) {
      try {
        // Make sure the srcObject is set
        if (localVideoRef.current.srcObject !== localStream) {
          localVideoRef.current.srcObject = localStream;
        }
        
        await localVideoRef.current.play();
        console.log("Video started playing after manual interaction");
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }
  };
  
  // Function to get grid layout dimensions
  const getGridLayout = () => {
    const count = Object.keys(remoteStreams).length + (localStream ? 1 : 0);
    
    // If there are no remote streams, show only local video at full size
    if (Object.keys(remoteStreams).length === 0) {
      return {
        container: "flex justify-center items-center",
        video: "w-full max-w-5xl h-[60vh]"
      };
    }
    
    // Define grid layout based on number of participants
    if (count <= 2) {
      return {
        container: "grid grid-cols-1 md:grid-cols-2 gap-4",
        video: "w-full h-[40vh]"
      };
    } else if (count <= 4) {
      return {
        container: "grid grid-cols-2 gap-4",
        video: "w-full h-[35vh]"
      };
    } else {
      return {
        container: "grid grid-cols-2 md:grid-cols-3 gap-2",
        video: "w-full h-[25vh]"
      };
    }
  };
  
  // Start the call
  const startCall = async () => {
    if (!callService) return;
    
    try {
      setCallStatus('connecting');
      setStatusMessage('Verifying Firebase permissions...');
      setError(null);
      
      // First initialize and verify Firebase collections
      const initResult = await initializeVideoCall(group.id, currentUserId);
      
      if (!initResult.success) {
        throw new Error(`Firebase setup error: ${initResult.error}`);
      }
      
      setStatusMessage('Accessing camera and microphone...');
      
      // Get local media stream with device selection
      const stream = await callService.getLocalStream(selectedCamera);
      setLocalStream(stream);
      
      // Set stream to local video element
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        
        // Force play the video
        try {
          await localVideoRef.current.play();
          console.log("Video started playing automatically");
        } catch (playError) {
          console.warn("Autoplay failed, may need user interaction:", playError);
          setStatusMessage('Please click the video area to start your camera');
        }
      }
      
      setStatusMessage('Initializing call...');
      
      // Start the call
      await callService.startCall();
      
      // Add current user to active participants list
      setActiveParticipants(prev => {
        if (!prev.includes(currentUserId)) {
          return [...prev, currentUserId];
        }
        return prev;
      });
      
      setCallStatus('active');
      setIsCallActive(true);
      setStatusMessage('');
    } catch (error) {
      console.error("Error starting video call:", error);
      setError(`Failed to start call: ${error.message}`);
      setCallStatus('error');
    }
  };
  
  // End the call
  const endCall = async () => {
    if (!callService) return;
    
    try {
      setStatusMessage('Ending call...');
      await callService.endCall();
      
      setLocalStream(null);
      setRemoteStreams({});
      setActiveParticipants([]);
      setCallStatus('ended');
      setIsCallActive(false);
      setStatusMessage('Call ended');
      
      // Notify parent component
      onCallEnd();
    } catch (error) {
      console.error("Error ending call:", error);
      setError(`Failed to end call properly: ${error.message}`);
    }
  };
  
  // Toggle video
  const toggleVideo = () => {
    if (!callService || !localStream) return;
    
    const enabled = !videoEnabled;
    callService.toggleVideo(enabled);
    setVideoEnabled(enabled);
  };
  
  // Toggle audio
  const toggleAudio = () => {
    if (!callService || !localStream) return;
    
    const enabled = !audioEnabled;
    callService.toggleAudio(enabled);
    setAudioEnabled(enabled);
  };
  
  // Retry with different display method
  const retryWithDifferentMethod = () => {
    setUseCanvas(!useCanvas);
  };

  // Get layout based on participant count
  const layout = getGridLayout();

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Status messages and errors */}
      {(statusMessage || error) && (
        <div className={`p-3 text-center ${error ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>
          {error || statusMessage}
        </div>
      )}
      
      {/* Video Grid */}
      <div 
        ref={videoContainerRef}
        className={`flex-1 p-4 ${layout.container} overflow-hidden`}
      >
        {/* Local video */}
        {localStream && (
          <div 
            className={`relative overflow-hidden rounded-lg bg-gray-900 shadow-lg ${layout.video}`}
          >
            {useCanvas ? (
              <canvas
                ref={canvasRef}
                className="w-full h-full object-cover"
                style={{ background: '#1a1a2e' }}
              />
            ) : (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted={true}
                onClick={manualPlayVideo}
                className={`w-full h-full object-cover ${!videoEnabled ? 'opacity-0' : ''}`}
                style={{ background: '#1a1a2e' }}
              />
            )}
            
            {!videoEnabled && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                <Camera className="h-16 w-16 text-gray-500" />
              </div>
            )}
            
            <div className="absolute bottom-0 left-0 p-2 bg-black bg-opacity-50 text-white text-sm rounded-tr-lg">
              You {!audioEnabled && '(muted)'}
            </div>
            
            <div className="absolute top-2 right-2 bg-black bg-opacity-50 p-2 rounded text-white text-xs">
              <button onClick={retryWithDifferentMethod} className="flex items-center">
                <RefreshCw size={12} className="mr-1" />
                Try {useCanvas ? "Video" : "Canvas"} Mode
              </button>
            </div>
          </div>
        )}
        
        {/* Remote videos */}
        {Object.entries(remoteStreams).map(([userId, stream]) => (
          <div 
            key={userId} 
            className={`relative overflow-hidden rounded-lg bg-gray-900 shadow-lg ${layout.video}`}
          >
            <video
              ref={el => {
                if (el) el.srcObject = stream;
              }}
              autoPlay
              playsInline
              muted={false}
              className="w-full h-full object-cover"
              style={{ background: '#1a1a2e' }}
            />
            <div className="absolute bottom-0 left-0 p-2 bg-black bg-opacity-50 text-white text-sm rounded-tr-lg">
              {userId === currentUserId ? 'You' : getFriendNameById(userId)}
            </div>
          </div>
        ))}
        
        {/* Placeholder when no participants */}
        {activeParticipants.length === 0 && callStatus !== 'active' && (
          <div className="col-span-full flex flex-col items-center justify-center text-center text-white p-8 h-[60vh]">
            <Video className="h-20 w-20 text-gray-500 mb-4" />
            <h3 className="text-xl font-bold mb-3">Start Video Call</h3>
            <p className="max-w-md text-gray-400 mb-6">
              Video calls allow you to study face-to-face with your group members in real-time.
            </p>
            
            {error && (
              <div className="bg-red-800 bg-opacity-30 p-4 rounded-lg mb-6 max-w-md">
                <div className="flex items-center text-red-300 mb-2">
                  <AlertTriangle className="mr-2" size={18} />
                  <span className="font-medium">Error</span>
                </div>
                <p className="text-sm text-red-100">{error}</p>
                <p className="text-xs text-red-300 mt-2">
                  Please make sure your camera is connected and not in use by another application.
                </p>
              </div>
            )}
            
            {/* Camera selection */}
            {deviceList.length > 0 && (
              <div className="mt-4 max-w-md w-full">
                <label className="block text-white text-sm mb-2">Select Camera:</label>
                <select 
                  className="w-full p-2 rounded bg-gray-700 text-white border border-gray-500"
                  value={selectedCamera}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                >
                  {deviceList.map(device => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${deviceList.indexOf(device) + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <button
              onClick={startCall}
              disabled={callStatus === 'connecting'}
              className={`mt-6 px-6 py-3 rounded-md font-medium flex items-center ${
                callStatus === 'connecting' 
                  ? 'bg-gray-500 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              <Camera className="mr-2" />
              {callStatus === 'connecting' ? 'Connecting...' : 'Start Video Call'}
            </button>
          </div>
        )}
      </div>
      
      {/* Call Controls */}
      {isCallActive && (
        <div className="bg-gray-800 p-4 flex items-center justify-between">
          <div className="text-white text-sm flex items-center">
            <Users className="mr-2" size={18} />
            <span>{activeParticipants.length} participant{activeParticipants.length !== 1 ? 's' : ''}</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <button 
              onClick={toggleAudio}
              className={`p-3 rounded-full ${audioEnabled ? 'bg-gray-600' : 'bg-red-500'}`}
            >
              {audioEnabled ? <Mic className="text-white" /> : <MicOff className="text-white" />}
            </button>
            
            <button 
              onClick={toggleVideo}
              className={`p-3 rounded-full ${videoEnabled ? 'bg-gray-600' : 'bg-red-500'}`}
            >
              {videoEnabled ? <Camera className="text-white" /> : <CameraOff className="text-white" />}
            </button>
            
            <button 
              onClick={endCall}
              className="p-3 rounded-full bg-red-600"
            >
              <PhoneOff className="text-white" />
            </button>
          </div>
          
          <div className="w-24">
            {/* Spacer to balance the layout */}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoCall;